import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

// ─── Types ────────────────────────────────────────────────────────────────────

interface OverviewResponse {
  member_id: number
  login_id: string
  name: string
  total_attendance_count: number
  consecutive_attendance_count: number
  monthly_attendance_count: number
  monthly_attendance_dates: string[]
  year: number
  month: number
  first_attendance_date: string
  last_attendance_date: string
}

interface HistoryResponse {
  member_id: number
  login_id: string
  name: string
  total_attendance_count: number
  first_attendance_date: string
  last_attendance_date: string
  attendance_dates: string[]
  skip: number
  limit: number
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminAttendanceDetail() {
  const { login_id } = useParams<{ login_id: string }>()
  const navigate = useNavigate()

  const [overview, setOverview] = useState<OverviewResponse | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(true)

  const [history, setHistory] = useState<string[]>([])
  const [historyTotal, setHistoryTotal] = useState(0)
  const [historyLoading, setHistoryLoading] = useState(true)
  const [historySkip, setHistorySkip] = useState(0)
  const historyLimit = 50

  // 달력 연/월 네비
  const today = new Date()
  const [calYear, setCalYear] = useState(today.getFullYear())
  const [calMonth, setCalMonth] = useState(today.getMonth() + 1)

  // ── fetch overview ─────────────────────────────────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      setOverviewLoading(true)
      try {
        const res = await fetchWithAuth(
          getApiUrl(`/marubit/api/attendance/admin/${login_id}/overview?year=${calYear}&month=${calMonth}`)
        )
        if (!res.ok) throw new Error()
        setOverview(await res.json())
      } catch {
        alert('출석 정보를 불러오는 데 실패했습니다.')
        navigate('/admin/attendance')
      } finally {
        setOverviewLoading(false)
      }
    }
    fetch()
  }, [login_id, calYear, calMonth])

  // ── fetch history ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      setHistoryLoading(true)
      try {
        const res = await fetchWithAuth(
          getApiUrl(`/marubit/api/attendance/admin/${login_id}/history?skip=${historySkip}&limit=${historyLimit}`)
        )
        if (!res.ok) throw new Error()
        const data: HistoryResponse = await res.json()
        setHistory(data.attendance_dates)
        setHistoryTotal(data.total_attendance_count)
      } catch {
        // silent
      } finally {
        setHistoryLoading(false)
      }
    }
    fetch()
  }, [login_id, historySkip])

  // ── calendar helpers ───────────────────────────────────────────────────────
  const attendedSet = new Set(overview?.monthly_attendance_dates ?? [])

  const getDaysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate()
  const getFirstDayOfWeek = (y: number, m: number) => new Date(y, m - 1, 1).getDay() // 0=Sun

  const prevMonth = () => {
    if (calMonth === 1) { setCalYear(y => y - 1); setCalMonth(12) }
    else setCalMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (calMonth === 12) { setCalYear(y => y + 1); setCalMonth(1) }
    else setCalMonth(m => m + 1)
  }

  const formatDate = (d: string | null) => {
    if (!d) return '-'
    return new Date(d).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  const toDateStr = (y: number, m: number, d: number) => {
    const mm = String(m).padStart(2, '0')
    const dd = String(d).padStart(2, '0')
    return `${y}-${mm}-${dd}`
  }

  const totalHistoryPages = Math.ceil(historyTotal / historyLimit)
  const currentHistoryPage = Math.floor(historySkip / historyLimit) + 1

  // ── render ─────────────────────────────────────────────────────────────────
  if (overviewLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-gray-300 text-[13px]">
        불러오는 중...
      </div>
    )
  }

  if (!overview) return null

  const daysInMonth = getDaysInMonth(calYear, calMonth)
  const firstDow = getFirstDayOfWeek(calYear, calMonth)
  const todayStr = toDateStr(today.getFullYear(), today.getMonth() + 1, today.getDate())

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[900px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">출석 상세</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/attendance')}
            className="text-[13px] text-[#887267] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] bg-white cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            ← 목록으로
          </button>
        </div>

        {/* ── 회원 정보 + 통계 카드 ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-6 py-5 mb-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
            {/* 이름/아이디 */}
            <div>
              <p className="text-[11px] text-gray-400 mb-0.5">회원</p>
              <p className="text-[16px] font-extrabold text-[#2a1f1a]">
                {overview.name}
                <span className="text-[13px] font-normal text-gray-400 ml-2">{overview.login_id}</span>
              </p>
            </div>

            <div className="w-px h-8 bg-[#f0e8e4] hidden sm:block" />

            {/* 통계 3종 */}
            <StatChip label="총 출석" value={overview.total_attendance_count} unit="일" accent />
            <StatChip label="연속 출석" value={overview.consecutive_attendance_count} unit="일" />
            <StatChip label="이번달 출석" value={overview.monthly_attendance_count} unit="일" />

            <div className="w-px h-8 bg-[#f0e8e4] hidden sm:block" />

            {/* 첫/마지막 출석 */}
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] text-gray-400">첫 출석</p>
              <p className="text-[12px] text-[#2a1f1a]">{formatDate(overview.first_attendance_date)}</p>
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] text-gray-400">최근 출석</p>
              <p className="text-[12px] text-[#2a1f1a]">{formatDate(overview.last_attendance_date)}</p>
            </div>
          </div>
        </div>

        {/* ── 달력 + 전체 기록 (2단) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* 달력 */}
          <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
            {/* 달력 헤더 */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={prevMonth}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#e8e0dc] bg-transparent text-[#887267] hover:bg-[#f5f2f0] cursor-pointer text-[14px] transition-colors"
              >
                ‹
              </button>
              <span className="text-[14px] font-bold text-[#2a1f1a]">
                {calYear}년 {calMonth}월
                <span className="ml-2 text-[12px] font-normal text-[#887267]">
                  {overview.monthly_attendance_count}일 출석
                </span>
              </span>
              <button
                onClick={nextMonth}
                className="w-7 h-7 flex items-center justify-center rounded-lg border border-[#e8e0dc] bg-transparent text-[#887267] hover:bg-[#f5f2f0] cursor-pointer text-[14px] transition-colors"
              >
                ›
              </button>
            </div>

            {/* 요일 헤더 */}
            <div className="grid grid-cols-7 mb-1">
              {['일', '월', '화', '수', '목', '금', '토'].map((d, i) => (
                <div
                  key={d}
                  className={`text-center text-[11px] font-medium py-1 ${
                    i === 0 ? 'text-red-400' : i === 6 ? 'text-blue-400' : 'text-gray-400'
                  }`}
                >
                  {d}
                </div>
              ))}
            </div>

            {/* 날짜 그리드 */}
            <div className="grid grid-cols-7 gap-y-1">
              {/* 빈 칸 */}
              {Array.from({ length: firstDow }).map((_, i) => (
                <div key={`empty-${i}`} />
              ))}
              {/* 날짜 */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateStr = toDateStr(calYear, calMonth, day)
                const attended = attendedSet.has(dateStr)
                const isToday = dateStr === todayStr
                const dow = (firstDow + day - 1) % 7

                return (
                  <div
                    key={day}
                    className={`flex items-center justify-center h-8 rounded-lg text-[13px] transition-colors ${
                      attended
                        ? 'bg-[#887267] text-white font-bold'
                        : isToday
                        ? 'border border-[#887267] text-[#887267] font-semibold'
                        : dow === 0
                        ? 'text-red-300'
                        : dow === 6
                        ? 'text-blue-300'
                        : 'text-gray-400'
                    }`}
                  >
                    {day}
                  </div>
                )
              })}
            </div>

            {/* 범례 */}
            <div className="flex items-center gap-3 mt-4 pt-3 border-t border-[#f5f2f0]">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-[#887267]" />
                <span className="text-[11px] text-gray-400">출석</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded border border-[#887267]" />
                <span className="text-[11px] text-gray-400">오늘</span>
              </div>
            </div>
          </div>

          {/* 전체 출석 기록 */}
          <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-5 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[13px] font-bold text-[#5a3d32]">전체 출석 기록</h2>
              <span className="text-[12px] text-gray-400">
                총 <span className="font-semibold text-[#887267]">{historyTotal}</span>일
              </span>
            </div>

            {historyLoading ? (
              <div className="flex-1 flex items-center justify-center text-gray-300 text-[13px]">
                불러오는 중...
              </div>
            ) : history.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-gray-300 text-[13px]">
                출석 기록이 없습니다.
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto max-h-[320px] pr-1">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {history.map((d, idx) => (
                      <div
                        key={d}
                        className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-[#fdf8f0] transition-colors"
                      >
                        <span className="text-[11px] text-gray-300 w-6 text-right">
                          {historySkip + idx + 1}
                        </span>
                        <span className="text-[13px] text-[#2a1f1a]">{formatDate(d)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 기록 페이지네이션 */}
                {totalHistoryPages > 1 && (
                  <div className="flex justify-center items-center gap-1.5 mt-4 pt-3 border-t border-[#f5f2f0]">
                    <button
                      onClick={() => setHistorySkip((currentHistoryPage - 2) * historyLimit)}
                      disabled={currentHistoryPage === 1}
                      className="w-7 h-7 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] transition-colors cursor-pointer"
                    >
                      ‹
                    </button>
                    <span className="text-[12px] text-gray-400">
                      {currentHistoryPage} / {totalHistoryPages}
                    </span>
                    <button
                      onClick={() => setHistorySkip(currentHistoryPage * historyLimit)}
                      disabled={currentHistoryPage === totalHistoryPages}
                      className="w-7 h-7 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] transition-colors cursor-pointer"
                    >
                      ›
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>
    </div>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatChip({
  label, value, unit, accent = false,
}: {
  label: string
  value: number
  unit: string
  accent?: boolean
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className={`text-[22px] font-extrabold leading-none ${accent ? 'text-[#887267]' : 'text-[#2a1f1a]'}`}>
        {value.toLocaleString()}
        <span className="text-[12px] font-normal text-gray-400 ml-0.5">{unit}</span>
      </p>
    </div>
  )
}